import { useCallback, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { countryApi, currencyApi, orgApi, userApi, workspaceApi } from '../api';
import { useAsync } from '../components/useAsync';
import PageShell from '../components/PageShell';
import DataTable from '../components/DataTable';
import type {
  CountryOption,
  CurrencyOption,
  Organization,
  Workspace,
  WorkspaceMember,
  WorkspaceRole,
} from '../types';
import { WORKSPACE_ROLE_LABEL } from '../types';

/**
 * Country options for one org, narrowed to what it is allowed to use.
 *
 * `selectable_countries` is the org's `allowed_countries` already resolved
 * server-side (every selectable market when it is null) and intersected with
 * the markets the measurement API supports — so it is exactly what the API
 * will accept, and an empty result is a real answer: the org's policy is `[]`
 * and no workspace can be created for it until someone widens it.
 *
 * `current` is the value already on the row, and it is always included even
 * when the policy above would exclude it. An antd Select given a value with no
 * matching option renders the bare code with no label, so an operator opening
 * a workspace pinned to a market its org no longer permits — a legacy row, or
 * one whose policy was narrowed afterwards — would see an unexplained "ZA"
 * where every other row reads "South Africa (ZA)", and could not tell whether
 * the field had loaded. Showing it, labelled and flagged, is also the only way
 * to say what has to change.
 */
function countryOptionsForOrg(
  countries: CountryOption[],
  org: Organization | undefined,
  current?: string | null,
) {
  const allowed = org ? new Set(org.selectable_countries) : null;
  const options = countries
    .filter((c) => allowed === null || allowed.has(c.iso2))
    .map((c) => ({ value: c.iso2, label: `${c.name} (${c.iso2})` }));

  if (current && !options.some((o) => o.value === current)) {
    // Only explain the absence once the market list has actually arrived.
    // While it is still loading every option is missing, and saying "not a
    // market Seiki measures" then would be a confident lie about a value that
    // is usually perfectly fine — the plain code is the honest placeholder.
    const known = countries.find((c) => c.iso2 === current);
    const label = !countries.length
      ? current
      : known
        ? `${known.name} (${current}) — current, no longer permitted`
        : `${current} — current, not a market Seiki measures`;
    options.unshift({ value: current, label });
  }
  return options;
}

/**
 * Default-currency options, "follow the market" first (DL-14).
 *
 * The empty value maps to `null` on the wire: no override, so the workspace
 * budgets in its market's currency and moves with it if the country is ever
 * re-pinned. That is the usual case, so it leads and names the currency it
 * resolves to rather than reading as "none". Narrowed to the allowed list
 * when one is picked, since the API refuses a default outside it.
 */
const FOLLOW_MARKET = '';

function defaultCurrencyOptions(
  currencies: CurrencyOption[],
  allowed: string[] | undefined,
  marketCurrency?: string | null,
) {
  const permitted = allowed?.length ? new Set(allowed) : null;
  const options = currencies
    .filter((c) => permitted === null || permitted.has(c.code))
    .map((c) => ({ value: c.code, label: `${c.name} (${c.code})` }));
  if (!marketCurrency || permitted === null || permitted.has(marketCurrency)) {
    options.unshift({
      value: FOLLOW_MARKET,
      label: marketCurrency ? `Market currency (${marketCurrency})` : 'Market currency',
    });
  }
  return options;
}

/**
 * The two currency fields, shared by the create and edit forms.
 *
 * An empty allowed list means every currency (`null` on the wire), the same
 * convention as an org's allowed countries. When the list leaves the market
 * currency out, the default is cleared so the operator has to pick one from
 * the list rather than submit a pair the API would refuse.
 */
function CurrencyFields({
  form,
  currencies,
  marketCurrency,
  loading,
}: {
  form: ReturnType<typeof Form.useForm>[0];
  currencies: CurrencyOption[];
  marketCurrency?: string | null;
  loading?: boolean;
}) {
  const allowed: string[] | undefined = Form.useWatch('allowed_currencies', form);
  const options = defaultCurrencyOptions(currencies, allowed, marketCurrency);
  return (
    <>
      <Form.Item
        name="allowed_currencies"
        label="Allowed currencies"
        extra="The currencies a budget in this workspace may be planned in. Leave empty to allow every currency."
      >
        <Select
          mode="multiple"
          allowClear
          optionFilterProp="label"
          loading={loading}
          placeholder="Every currency"
          options={currencies.map((c) => ({ value: c.code, label: `${c.name} (${c.code})` }))}
          onChange={(next: string[]) => {
            const current = form.getFieldValue('currency') ?? FOLLOW_MARKET;
            const effective = current || marketCurrency;
            if (next.length && effective && !next.includes(effective)) {
              form.setFieldsValue({ currency: next[0] });
            }
          }}
        />
      </Form.Item>
      <Form.Item
        name="currency"
        label="Default currency"
        rules={[{ required: true, message: 'Pick a default from the allowed currencies' }]}
        extra="What new budgets start in. Leave on the market currency unless this market is sold in another one."
      >
        <Select showSearch optionFilterProp="label" loading={loading} options={options} />
      </Form.Item>
    </>
  );
}

/** Form values to the wire shape: '' and [] both mean "unset" (null). */
function currencyPayload(values: { currency?: string; allowed_currencies?: string[] }) {
  return {
    currency: values.currency || null,
    allowed_currencies: values.allowed_currencies?.length ? values.allowed_currencies : null,
  };
}

export default function WorkspacesPage() {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [orgFilter, setOrgFilter] = useState<number | undefined>();
  const [createOpen, setCreateOpen] = useState(false);
  const [membersOf, setMembersOf] = useState<Workspace | null>(null);
  const [renaming, setRenaming] = useState<Workspace | null>(null);
  const [repinning, setRepinning] = useState<Workspace | null>(null);
  const [recurrencying, setRecurrencying] = useState<Workspace | null>(null);
  // Every open remounts the row-seeded modals below. antd's form store
  // outlives the mounted <Form> and `initialValues` only fills the keys the
  // store does not already hold, so a reused instance shows the row opened
  // before it — the workspace clicked second kept the first one's country.
  // These counters are the modals' React keys: bumped on open and never on
  // close, so each open gets a fresh store and a closing modal still animates
  // out. Re-seeding from an effect instead cannot work — the effect runs
  // before the modal's portal has mounted the form it means to re-seed.
  const [renameOpens, setRenameOpens] = useState(0);
  const [repinOpens, setRepinOpens] = useState(0);
  const [currencyOpens, setCurrencyOpens] = useState(0);

  function openRename(row: Workspace) {
    setRenaming(row);
    setRenameOpens((n) => n + 1);
  }

  function openRepin(row: Workspace) {
    setRepinning(row);
    setRepinOpens((n) => n + 1);
  }

  function openCurrency(row: Workspace) {
    setRecurrencying(row);
    setCurrencyOpens((n) => n + 1);
  }

  const orgs = useAsync(() => orgApi.options(), []);
  // The paged org list carries `selectable_countries`; the picker-shaped
  // `options()` does not, and both country forms need it to know what this org
  // may use.
  const orgRows = useAsync(() => orgApi.list({ per_page: 100 }), []);
  const countries = useAsync(() => countryApi.list(), []);
  const currencies = useAsync(() => currencyApi.list(), []);
  const workspaces = useAsync(
    () =>
      workspaceApi.list({
        page,
        per_page: perPage,
        q: query || undefined,
        org_id: orgFilter,
      }),
    [page, perPage, query, orgFilter],
  );

  const columns: ColumnsType<Workspace> = [
    {
      title: 'Workspace',
      key: 'name',
      render: (_, row) => (
        <div>
          <strong>{row.name}</strong>
          <div className="mono" style={{ color: '#6b7280' }}>
            {row.slug}
          </div>
        </div>
      ),
    },
    { title: 'Organization', dataIndex: 'org_name', key: 'org_name' },
    {
      title: 'Tenant',
      dataIndex: 'tenant_subdomain',
      key: 'tenant_subdomain',
      width: 130,
      render: (v: string | null) =>
        v ? <span className="mono">{v}</span> : <Tag>Bare domain</Tag>,
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 110,
      // Mandatory since AD-32: it is the market this workspace's inventory is
      // measured against, so there is no "unset" to render any more.
      //
      // The value is the control. Editing a country is rare and always starts
      // from reading the current one, so the cell that shows it is where the
      // hand already is — a separate action button would sit at the far end of
      // the row, away from the thing it changes.
      render: (v: string, row) => (
        <Tooltip title={`Pinned to ${v} — click to change the market`}>
          <Tag
            onClick={() => openRepin(row)}
            style={{ cursor: 'pointer', marginInlineEnd: 0 }}
          >
            {v}
          </Tag>
        </Tooltip>
      ),
    },
    {
      title: 'Currency',
      key: 'currency',
      width: 120,
      // Same pattern as Country: the value is the control. An override is
      // coloured so it stands out from the usual "follows the market" case.
      render: (_, row) => {
        const others = (row.allowed_currencies ?? []).filter(
          (c) => c !== row.effective_currency,
        );
        const defaultNote = row.currency
          ? `Defaults to ${row.currency}, overriding the ${row.country} market currency.`
          : `Defaults to the ${row.country} market currency.`;
        const listNote = row.allowed_currencies
          ? ` Allows ${row.allowed_currencies.join(', ')}.`
          : ' Allows every currency.';
        return (
          <Tooltip title={`${defaultNote}${listNote} Click to change.`}>
            <Tag
              color={row.currency || row.allowed_currencies ? 'gold' : undefined}
              onClick={() => openCurrency(row)}
              style={{ cursor: 'pointer', marginInlineEnd: 0 }}
            >
              {row.effective_currency ?? '?'}
              {others.length ? ` +${others.length}` : ''}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Members',
      key: 'members',
      width: 130,
      render: (_, row) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => setMembersOf(row)}>
          {row.member_count} member{row.member_count === 1 ? '' : 's'}
        </Button>
      ),
    },
    {
      title: 'Flags',
      key: 'flags',
      render: (_, row) => (
        <Space size={4}>
          {!row.is_active && <Tag color="red">Inactive</Tag>}
          {row.is_shared_partner_space && <Tag color="geekblue">Shared partner</Tag>}
        </Space>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, row) => (
        <Button size="small" type="text" onClick={() => openRename(row)}>
          Rename
        </Button>
      ),
    },
  ];

  const applySearch = useCallback(() => {
    setPage(1);
    setQuery(search.trim());
  }, [search]);

  return (
    <PageShell
      title="Workspaces"
      subtitle={
        <>
          Collaboration containers holding inventory and campaigns. Creation used to exist
          only on the customer API, so adding a second workspace meant signing in as one of
          the org&apos;s admins.
        </>
      }
      actions={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={workspaces.reload}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Create workspace
          </Button>
        </Space>
      }
      toolbar={
        <>
          <Input
            placeholder="Search workspace, slug or org"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={applySearch}
            style={{ width: 280 }}
            allowClear
            onClear={() => {
              setSearch('');
              setQuery('');
              setPage(1);
            }}
          />
          <Button onClick={applySearch}>Search</Button>
          <Select
            placeholder="All organizations"
            style={{ width: 220 }}
            allowClear
            loading={orgs.loading}
            value={orgFilter}
            onChange={(v) => {
              setOrgFilter(v);
              setPage(1);
            }}
            options={(orgs.data ?? []).map((o) => ({ value: o.id, label: o.name }))}
          />
        </>
      }
    >
      <DataTable<Workspace>
        rowKey="id"
        loading={workspaces.loading}
        columns={columns}
        dataSource={workspaces.data?.items ?? []}
        pagination={{
          current: page,
          pageSize: perPage,
          total: workspaces.data?.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `${total} workspaces`,
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPerPage(nextSize);
          },
        }}
      />

      <CreateWorkspaceModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={workspaces.reload}
        orgRows={orgRows.data?.items ?? []}
        countries={countries.data?.items ?? []}
        currencies={currencies.data?.items ?? []}
        loading={orgRows.loading || countries.loading}
      />
      <MembersDrawer
        workspace={membersOf}
        onClose={() => setMembersOf(null)}
        onChanged={workspaces.reload}
      />
      <RenameWorkspaceModal
        key={`rename-${renameOpens}`}
        workspace={renaming}
        onClose={() => setRenaming(null)}
        onRenamed={workspaces.reload}
      />
      <SetWorkspaceCountryModal
        key={`repin-${repinOpens}`}
        workspace={repinning}
        onClose={() => setRepinning(null)}
        onChanged={workspaces.reload}
        orgRows={orgRows.data?.items ?? []}
        countries={countries.data?.items ?? []}
      />
      <SetWorkspaceCurrencyModal
        key={`currency-${currencyOpens}`}
        workspace={recurrencying}
        onClose={() => setRecurrencying(null)}
        onChanged={workspaces.reload}
        countries={countries.data?.items ?? []}
        currencies={currencies.data?.items ?? []}
      />
    </PageShell>
  );
}

function CreateWorkspaceModal({
  open,
  onClose,
  onCreated,
  orgRows,
  countries,
  currencies,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  orgRows: Organization[];
  countries: CountryOption[];
  currencies: CurrencyOption[];
  loading: boolean;
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [orgId, setOrgId] = useState<number | undefined>();
  const pickedCountry: string | undefined = Form.useWatch('country', form);
  const marketCurrency = countries.find((c) => c.iso2 === pickedCountry)?.currency;

  const orgs = useAsync(() => orgApi.options(), []);

  // The org's geo policy is the real constraint on this form: the API rejects
  // anything outside it, so offer exactly what it permits (AD-32 — this used to
  // read the *tenant's* allowed_countries, which said nothing for an org on the
  // bare Seiki domain).
  const selectedOrg = orgRows.find((o) => o.id === orgId);
  const countryOptions = countryOptionsForOrg(countries, selectedOrg);
  const orgBlocked = selectedOrg !== undefined && countryOptions.length === 0;

  // Only users already in the target org may be seeded as workspace admin.
  const candidateAdmins = useAsync(
    () => (orgId ? userApi.list({ org_id: orgId, per_page: 100 }) : Promise.resolve(null)),
    [orgId],
  );

  async function onSubmit() {
    const values = await form.validateFields();
    setSubmitting(true);
    try {
      const created = await workspaceApi.create({
        org_id: values.org_id,
        name: values.name.trim(),
        country: values.country,
        ...currencyPayload(values),
        admin_user_id: values.admin_user_id ?? null,
      });
      message.success(`Created workspace "${created.slug}"`);
      form.resetFields();
      setOrgId(undefined);
      onCreated();
      onClose();
    } catch {
      /* toast already shown */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Create workspace"
      open={open}
      onCancel={onClose}
      onOk={onSubmit}
      okText="Create"
      confirmLoading={submitting}
      width={540}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ currency: FOLLOW_MARKET, allowed_currencies: [] }}
      >
        <Form.Item
          name="org_id"
          label="Organization"
          rules={[{ required: true, message: 'Pick an organization' }]}
        >
          <Select
            loading={orgs.loading}
            placeholder="Select organization"
            onChange={(value: number) => {
              setOrgId(value);
              // Preselect the org's default market — the same value the API
              // would fall back to — so the common case is one click and an
              // override is deliberate.
              const picked = orgRows.find((o) => o.id === value);
              form.setFieldsValue({
                country: picked?.default_country ?? undefined,
                admin_user_id: undefined,
              });
            }}
            options={(orgs.data ?? []).map((o) => ({
              value: o.id,
              label: o.tenant_subdomain ? `${o.name} (${o.tenant_subdomain})` : o.name,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Name is required' }]}
          extra="The slug is derived from this and frozen at creation. The name can be changed later; the slug cannot."
        >
          <Input placeholder="Acme France" />
        </Form.Item>

        <Form.Item
          name="country"
          label="Country"
          rules={[{ required: true, message: 'Country is required' }]}
          extra={
            orgBlocked
              ? "This organization's allowed countries list is empty, so no workspace can be created for it. Widen it on the Organizations screen first."
              : selectedOrg && selectedOrg.allowed_countries
                ? `${selectedOrg.name} is restricted to ${selectedOrg.allowed_countries.join(', ')}. A workspace holds inventory for one market, and its KPIs are measured against that market's model — this cannot be changed later once the workspace holds inventory in it.`
                : 'A workspace holds inventory for one market, and its KPIs are measured against that market\u2019s model. Only markets the measurement API supports are offered.'
          }
        >
          <Select
            showSearch
            optionFilterProp="label"
            disabled={!orgId || orgBlocked}
            loading={loading}
            placeholder={orgId ? 'Select country' : 'Select an organization first'}
            options={countryOptions}
          />
        </Form.Item>

        <CurrencyFields
          form={form}
          currencies={currencies}
          marketCurrency={marketCurrency}
          loading={loading}
        />

        <Form.Item
          name="admin_user_id"
          label="Seed workspace admin"
          extra="Optional. Without a member, nobody in the customer org can see this workspace."
        >
          <Select
            allowClear
            disabled={!orgId}
            loading={candidateAdmins.loading}
            placeholder={orgId ? 'No initial member' : 'Select an organization first'}
            options={(candidateAdmins.data?.items ?? []).map((u) => ({
              value: u.id,
              label: `${u.first_name} ${u.last_name} — ${u.email}`,
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

/**
 * Re-pin a workspace to another measurement market.
 *
 * The slug is frozen at creation (D13) but the country is not, and the
 * asymmetry is the point: a wrong slug is an addressing mistake nobody can fix
 * without breaking saved links, while a wrong country is a *measurement*
 * mistake — the workspace's inventory is submitted against the wrong market's
 * model — and leaving it wrong is worse than changing it.
 *
 * The API refuses the change when the workspace already holds inventory in a
 * different country. Those faces were measured against their own market and
 * have to move first, which is an ops decision rather than a side effect of
 * this dropdown, so the form surfaces the refusal instead of trying to avoid it.
 */
function SetWorkspaceCountryModal({
  workspace,
  onClose,
  onChanged,
  orgRows,
  countries,
}: {
  workspace: Workspace | null;
  onClose: () => void;
  onChanged: () => void;
  orgRows: Organization[];
  countries: CountryOption[];
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const org = orgRows.find((o) => o.id === workspace?.org_id);
  const countryOptions = countryOptionsForOrg(countries, org, workspace?.country);

  async function onSubmit() {
    if (!workspace) return;
    const values = await form.validateFields();
    if (values.country === workspace.country) {
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      await workspaceApi.setCountry(workspace.id, values.country);
      message.success(`${workspace.name} is now pinned to ${values.country}`);
      onChanged();
      onClose();
    } catch {
      /* toast already shown */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Set workspace country"
      open={workspace !== null}
      onCancel={onClose}
      onOk={onSubmit}
      okText="Change country"
      confirmLoading={submitting}
      width={520}
      destroyOnClose
    >
      {workspace && (
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{ country: workspace.country }}
          onFinish={onSubmit}
        >
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="This decides which market the inventory is measured against"
            description={
              <>
                <span className="mono">{workspace.slug}</span> is pinned to{' '}
                <strong>{workspace.country}</strong>. Changing it re-points every future
                KPI submission for this workspace. It will be refused if the workspace
                already holds billboards in another country — move those first.
              </>
            }
          />
          <Form.Item
            name="country"
            label="Country"
            rules={[{ required: true, message: 'Country is required' }]}
            extra={
              org?.allowed_countries
                ? `${org.name} is restricted to ${org.allowed_countries.join(', ')}.`
                : 'Only markets the measurement API supports are offered.'
            }
          >
            <Select
              showSearch
              optionFilterProp="label"
              loading={!countries.length}
              options={countryOptions}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}

function RenameWorkspaceModal({
  workspace,
  onClose,
  onRenamed,
}: {
  workspace: Workspace | null;
  onClose: () => void;
  onRenamed: () => void;
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit() {
    if (!workspace) return;
    const values = await form.validateFields();
    const name = values.name.trim();
    if (name === workspace.name) {
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      await workspaceApi.rename(workspace.id, name);
      message.success(`Renamed to "${name}"`);
      onRenamed();
      onClose();
    } catch {
      /* toast already shown */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Rename workspace"
      open={workspace !== null}
      onCancel={onClose}
      onOk={onSubmit}
      okText="Rename"
      confirmLoading={submitting}
      width={480}
      destroyOnClose
    >
      {workspace && (
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{ name: workspace.name }}
          onFinish={onSubmit}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Name is required' }]}
            extra={
              <>
                The slug stays <span className="mono">{workspace.slug}</span> — it addresses
                this workspace in customer URLs, so renaming leaves it alone. Another
                workspace in {workspace.org_name} cannot already carry the new name.
              </>
            }
          >
            <Input autoFocus />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}

function MembersDrawer({
  workspace,
  onClose,
  onChanged,
}: {
  workspace: Workspace | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<number | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const members = useAsync(
    () => (workspace ? workspaceApi.members(workspace.id) : Promise.resolve(null)),
    [workspace?.id],
  );

  const mutate = useCallback(
    async (userId: number, action: () => Promise<unknown>, successText: string) => {
      setBusy(userId);
      try {
        await action();
        message.success(successText);
        await members.reload();
        onChanged();
      } catch {
        /* toast already shown */
      } finally {
        setBusy(null);
      }
    },
    [members, onChanged],
  );

  const columns: ColumnsType<WorkspaceMember> = [
    {
      title: 'Member',
      key: 'member',
      render: (_, row) => (
        <div>
          {row.first_name} {row.last_name}
          <div className="mono" style={{ color: '#6b7280' }}>
            {row.email}
          </div>
        </div>
      ),
    },
    {
      title: 'Role',
      key: 'role',
      width: 170,
      render: (_, row) => (
        <Select<WorkspaceRole>
          size="small"
          style={{ width: 150 }}
          value={row.role}
          loading={busy === row.user_id}
          onChange={(value) =>
            mutate(
              row.user_id,
              () => workspaceApi.updateMemberRole(workspace!.id, row.user_id, value),
              'Role updated',
            )
          }
          options={(Object.keys(WORKSPACE_ROLE_LABEL) as WorkspaceRole[]).map((value) => ({
            value,
            label: WORKSPACE_ROLE_LABEL[value],
          }))}
        />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, row) => (
        <Popconfirm
          title="Remove from workspace?"
          description="They keep their account and other memberships."
          onConfirm={() =>
            mutate(
              row.user_id,
              () => workspaceApi.removeMember(workspace!.id, row.user_id),
              'Member removed',
            )
          }
        >
          <Button size="small" danger type="text">
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <Drawer
      title={workspace ? `Members of ${workspace.name}` : 'Members'}
      width={620}
      open={workspace !== null}
      onClose={onClose}
      destroyOnClose
      extra={
        <Button type="primary" size="small" onClick={() => setAddOpen(true)}>
          Add member
        </Button>
      }
    >
      {workspace && (
        <>
          <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
            Only users whose organization owns this workspace can be members. The last{' '}
            <span className="mono">workspace_admin</span> cannot be removed &mdash; promote
            someone else first.
          </Typography.Paragraph>
          <Table<WorkspaceMember>
            rowKey="user_id"
            size="small"
            loading={members.loading}
            columns={columns}
            dataSource={members.data?.items ?? []}
            pagination={false}
            locale={{
              emptyText: (
                <Alert
                  type="warning"
                  showIcon
                  style={{ margin: 12 }}
                  message="No members"
                  description="Nobody in the customer org can see this workspace until someone is added."
                />
              ),
            }}
          />
          <AddMemberModal
            workspace={workspace}
            open={addOpen}
            onClose={() => setAddOpen(false)}
            onAdded={() => {
              members.reload();
              onChanged();
            }}
          />
        </>
      )}
    </Drawer>
  );
}

function AddMemberModal({
  workspace,
  open,
  onClose,
  onAdded,
}: {
  workspace: Workspace;
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [userId, setUserId] = useState<number | undefined>();
  const [role, setRole] = useState<WorkspaceRole>('workspace_contributor');
  const [submitting, setSubmitting] = useState(false);

  const candidates = useAsync(
    () => (open ? userApi.list({ org_id: workspace.org_id, per_page: 100 }) : Promise.resolve(null)),
    [open, workspace.org_id],
  );

  async function onSubmit() {
    if (!userId) return;
    setSubmitting(true);
    try {
      await workspaceApi.addMember(workspace.id, userId, role);
      message.success('Member added');
      setUserId(undefined);
      onAdded();
      onClose();
    } catch {
      /* toast already shown */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={`Add a member to ${workspace.name}`}
      open={open}
      onCancel={onClose}
      onOk={onSubmit}
      okText="Add"
      okButtonProps={{ disabled: !userId }}
      confirmLoading={submitting}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <div>
          <Typography.Text strong>User</Typography.Text>
          <Select
            style={{ width: '100%', marginTop: 6 }}
            loading={candidates.loading}
            placeholder={`Users in ${workspace.org_name}`}
            value={userId}
            onChange={setUserId}
            showSearch
            optionFilterProp="label"
            options={(candidates.data?.items ?? []).map((u) => ({
              value: u.id,
              label: `${u.first_name} ${u.last_name} — ${u.email}`,
            }))}
          />
        </div>
        <div>
          <Typography.Text strong>Role</Typography.Text>
          <Select<WorkspaceRole>
            style={{ width: '100%', marginTop: 6 }}
            value={role}
            onChange={setRole}
            options={(Object.keys(WORKSPACE_ROLE_LABEL) as WorkspaceRole[]).map((value) => ({
              value,
              label: WORKSPACE_ROLE_LABEL[value],
            }))}
          />
        </div>
      </Space>
    </Modal>
  );
}

/**
 * Set the currencies a workspace's budgets may use, and the default (DL-14).
 *
 * Unlike the country, nothing already planned is affected: campaigns and
 * media plans keep the currency they were saved with.
 */
function SetWorkspaceCurrencyModal({
  workspace,
  onClose,
  onChanged,
  countries,
  currencies,
}: {
  workspace: Workspace | null;
  onClose: () => void;
  onChanged: () => void;
  countries: CountryOption[];
  currencies: CurrencyOption[];
}) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const marketCurrency = countries.find((c) => c.iso2 === workspace?.country)?.currency;

  async function onSubmit() {
    if (!workspace) return;
    const values = await form.validateFields();
    const next = currencyPayload(values);
    if (
      next.currency === workspace.currency &&
      (next.allowed_currencies ?? []).join() === (workspace.allowed_currencies ?? []).join() &&
      (next.allowed_currencies === null) === (workspace.allowed_currencies === null)
    ) {
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      const updated = await workspaceApi.setCurrencies(workspace.id, next);
      message.success(`${workspace.name} now defaults to ${updated.effective_currency}`);
      onChanged();
      onClose();
    } catch {
      /* toast already shown */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Set workspace currencies"
      open={workspace !== null}
      onCancel={onClose}
      onOk={onSubmit}
      okText="Save"
      confirmLoading={submitting}
      width={520}
      destroyOnClose
    >
      {workspace && (
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{
            currency: workspace.currency ?? FOLLOW_MARKET,
            allowed_currencies: workspace.allowed_currencies ?? [],
          }}
          onFinish={onSubmit}
        >
          <CurrencyFields
            form={form}
            currencies={currencies}
            marketCurrency={marketCurrency}
            loading={!currencies.length}
          />
          <Typography.Text type="secondary">
            Campaigns and media plans already saved keep their own currency.
          </Typography.Text>
        </Form>
      )}
    </Modal>
  );
}
